import React from 'react';
import { Search, Filter, Calendar, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../Store/ApiContext';

export const DepositReportFilters = ({
  searchTerm,
  onSearchChange,
  userFilter,
  onUserFilterChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  requestIdFilter,
  onRequestIdFilterChange,
  recordTypeFilter,
  onRecordTypeFilterChange,
  showAdvancedFilters,
  onToggleAdvancedFilters,
  onClearFilters,
  onQuickDateFilter,
  activeQuickFilter,
  uniqueUsers,
  hasActiveFilters,
  isLoading
}) => {
  const { t } = useTranslation();
  const { ProtectedComponentRoles } = useApi();

  const quickFilters = [
    { key: 'week', label: t('last7Days') },
    { key: 'lastMonth', label: t('lastMonth') },
    { key: 'thisMonth', label: t('thisMonth') }
  ];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        {t('filtersAndSearch')}
      </h3>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search size={20} className="absolute start-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full ps-10 pe-5 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
          />
        </div>
      </div>

      {/* Record Type Filter */}
      <div className="mb-4">
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => onRecordTypeFilterChange('all')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${recordTypeFilter === 'all'
              ? 'bg-slate-600 text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
          >
            {t('allTransactions')}
          </button>
          <button
            onClick={() => onRecordTypeFilterChange('deposit')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${recordTypeFilter === 'deposit'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
          >
            {t('depositsOnly')}
          </button>
          <button
            onClick={() => onRecordTypeFilterChange('refund')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${recordTypeFilter === 'refund'
              ? 'bg-red-600 text-white shadow-md'
              : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
          >
            {t('refundsOnly')}
          </button>
        </div>
      </div>

      {/* Quick Date Filters */}
      <div className="mb-4">
        <div className="grid grid-cols-3 gap-3">
          {quickFilters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => {
                console.log(filter.key); onQuickDateFilter(filter.key);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeQuickFilter === filter.key
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Filters Toggle */}
      <div className="flex items-center justify-between">
        <button
          onClick={onToggleAdvancedFilters}
          className={`flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200 ${showAdvancedFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
            }`}
        >
          <Filter size={16} />
          <span>{showAdvancedFilters ? t('hideFilters') : t('moreFilters')}</span>
        </button>
        <div>
          {isLoading && <Loader2 size={16} className="animate-spin" />}
        </div>

        <button
          disabled={!hasActiveFilters}
          onClick={onClearFilters}
          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
        >
          {hasActiveFilters && (
            <>
              <X size={16} />
              <span>{t('clearFilters')}</span>
            </>
          )}
        </button>

      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ProtectedComponentRoles roles={['General Manager', 'System Admin']}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('userFilter')}
                </label>
                <select
                  value={userFilter}
                  onChange={(e) => onUserFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('allUsers')}</option>
                  {uniqueUsers.map(user => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>
            </ProtectedComponentRoles>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('dateFrom')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('dateTo')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('requestId')}
              </label>
              <input
                type="text"
                placeholder={t('requestIdPlaceholder')}
                value={requestIdFilter}
                onChange={(e) => onRequestIdFilterChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositReportFilters;